import { FormHelperText, InputLabel, Typography } from '@mui/material';
import { useField } from 'formik';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-media';
import styled from 'styled-components';

import AbsenceTypeSelect from '@/components/common/forms/FormikFields/AbsenceTypeSelect/AbsenceTypeSelect';
import ComponentDisabler from '@/components/common/Loaders/ComponentDisabler';
import { COLOR, GLOBAL_MEDIA_QUERIES, MEDIA_QUERIES_BREAKPOINTS, PX_TO_REM } from '@/globalStyles';
import { LimitsUsage } from '@/hooks/useUsedLimits/useUsedLimits';
import SingleRequestTypeCard from '@/pages/NewRequest/RequestTypeCards/SingleRequestTypeCard';
import { NEW_REQUEST_FIELD } from '@/pages/NewRequest/useNewRequestLogic';

interface IRequestTypeCardsProps {
    className?: string;
    limitsUsage?: LimitsUsage[];
    disabled: boolean;
}

const MAX_CARDS_SHOWN = 8;

const RequestTypeCards = ({ limitsUsage, disabled, className }: IRequestTypeCardsProps) => {
    const { t } = useTranslation();
    const [{ value: selectedRequestType }, { error, touched }, { setValue }] = useField({
        name: NEW_REQUEST_FIELD.selectedRequestType,
    });

    const matchTablet = useMedia({ query: GLOBAL_MEDIA_QUERIES.ltablet });
    const matchSmallTablet = useMedia({ query: GLOBAL_MEDIA_QUERIES.stablet });

    const showSelect = useMemo(
        () => !matchTablet || (limitsUsage && limitsUsage?.length > MAX_CARDS_SHOWN),
        [matchTablet, limitsUsage]
    );

    return (
        <ComponentDisabler isDisabled={disabled}>
            <div className={className}>
                {showSelect ? (
                    <SelectWrapper>
                        {matchSmallTablet && (
                            <InputLabel>
                                <Typography color={COLOR.darkGray} variant="table_text">
                                    {t('newApplication_typePicker_title')}
                                </Typography>
                            </InputLabel>
                        )}
                        <AbsenceTypeSelect
                            name={NEW_REQUEST_FIELD.selectedRequestType}
                            absenceTypes={limitsUsage?.map(l => ({
                                ...l.absenceType,
                                noLimits: l.limit?.noLimits,
                                daysLeft: l.daysLeft,
                            }))}
                            placeholder={t('newApplication_typePicker_title')}
                        />
                    </SelectWrapper>
                ) : (
                    <CardsWrapper>
                        <Cards>
                            {limitsUsage?.map(limitUsage => (
                                <SingleRequestTypeCard
                                    key={limitUsage?.absenceTypeId}
                                    absenceType={limitUsage?.absenceType}
                                    daysLeft={limitUsage?.daysLeft}
                                    noLimits={limitUsage?.limit?.noLimits}
                                    setValue={setValue}
                                    isSelected={selectedRequestType?._id === limitUsage?.absenceTypeId}
                                />
                            ))}
                        </Cards>
                        {touched && error ? <FormHelperText error>{error}</FormHelperText> : null}
                    </CardsWrapper>
                )}
            </div>
        </ComponentDisabler>
    );
};

const Cards = styled.div`
    display: flex;
    flex-wrap: wrap;
`;
const CardsWrapper = styled.div`
    margin-bottom: ${PX_TO_REM[20]};
`;

const SelectWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;

    & > div {
        width: 60%;

        @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
            width: 100%;
        }
    }
`;

const Memoized = memo(RequestTypeCards);
const Styled = styled(Memoized)`
    padding-bottom: ${PX_TO_REM[20]};
`;

export default Styled;
